// Light / dark / system, reachable from every page. It writes the same reader
// setting the Aa popover does, so the two always agree.
import type { ReaderSettings } from "@bibleunleashed/shared";
import { useEffect, useRef, useState } from "react";
import { useSettings } from "@/lib/settings";
import { Icon } from "./Icon";

type Theme = ReaderSettings["theme"];

const THEMES: { v: Theme; label: string; icon: "sun" | "moon" | "monitor" }[] = [
  { v: "light", label: "Light", icon: "sun" },
  { v: "dark", label: "Dark", icon: "moon" },
  { v: "system", label: "System", icon: "monitor" },
];

export function ThemeMenu() {
  const { settings, update } = useSettings();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const current = THEMES.find((t) => t.v === settings.theme) ?? THEMES[2]!;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (box.current && !e.composedPath().includes(box.current)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => document.addEventListener("click", onClick), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.removeEventListener("click", onClick);
    };
  }, [open]);

  return (
    <div ref={box} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Theme: ${current.label}`}
        title={`Theme: ${current.label}`}
        className="grid size-[34px] place-items-center rounded-md text-ink-2 hover:bg-paper-2 hover:text-ink"
      >
        <Icon name={current.icon} />
      </button>
      {open && (
        <div role="menu" aria-label="Theme" className="absolute top-[38px] right-0 z-30 w-[148px] rounded-lg border border-rule bg-paper py-1 shadow-pop">
          {THEMES.map((t) => (
            <button
              key={t.v}
              role="menuitemradio"
              aria-checked={t.v === settings.theme}
              onClick={() => {
                update({ theme: t.v });
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-[13px] hover:bg-paper-2 ${t.v === settings.theme ? "text-ink" : "text-ink-2"}`}
            >
              <Icon name={t.icon} width="15" height="15" />
              {t.label}
              {t.v === settings.theme && <Icon name="check" width="14" height="14" className="ml-auto text-accent" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
